/**
 * Fetches JSON from a URL with Bearer token authentication.
 * Extracted from functions.ts for testability; used by getAttributeFromUrl and Fetch Instrument Group taskpane.
 */

/** Thrown when no token is available; fetch must not be called. */
export class AuthRequiredError extends Error {
	constructor(message = "Authentication required") {
		super(message)
		this.name = "AuthRequiredError"
	}
}

/** Thrown when the API returns 4xx/5xx; includes status for 401, 404, etc. */
export class ApiError extends Error {
	constructor(
		public readonly status: number,
		message: string,
	) {
		super(message)
		this.name = "ApiError"
	}
}

export type FetchFn = typeof fetch

/**
 * Fetches JSON from a URL with Bearer token.
 * @param url - Full API URL to fetch
 * @param getToken - Returns the access token, or null if not authenticated
 * @param fetchFn - Optional fetch implementation (for testing)
 * @returns Parsed JSON on success
 * @throws AuthRequiredError when getToken returns null
 * @throws ApiError when response is not ok (401, 404, etc.)
 */
export async function fetchJsonWithAuth(
	url: string,
	getToken: () => Promise<string | null>,
	fetchFn: FetchFn = fetch,
): Promise<unknown> {
	const token = await getToken()
	if (!token) {
		throw new AuthRequiredError()
	}

	const response = await fetchFn(url, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}` },
	})

	if (!response.ok) {
		throw new ApiError(response.status, response.statusText)
	}

	return response.json()
}
